import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import cx from 'classnames';
import IconButton from 'plaid-threads/IconButton';
import CloseS1 from 'plaid-threads/Icons/CloseS1';
import Image from 'src/components/docs/Image';

import { ROUTES } from '../constants/routeMap';
import { CORE_EXCHANGE_DOCS_BASE_URL } from '../constants';
import { NavItem } from '../../docs/constants/routeMap';
import { stripTrailingSlash } from '../../docs/utilities';
import SidebarSubnav from '../../docs/Sidebar/SidebarSubnav';
import SidebarNavGroup from '../../docs/Sidebar/SidebarNavGroup';
import sidebarStyles from '../../docs/Sidebar/index.module.scss';

export interface Props {
  className?: string;
  mobile?: boolean;
  closed?: boolean;
  onClose?: () => void;
  baseUrl?: string;
}

/**
 * Find the parent section (e.g. "Getting started") that contains
 * the current page, and return it as the currentRoute. This gives
 * SidebarSubnav the section title/icon as the header and the
 * sibling pages as nav items.
 */
interface SidebarRoute {
  parentSection?: NavItem;
  currentItem?: NavItem;
}

const resolveRoute = (pathname: string): SidebarRoute => {
  const routeWithoutBase = pathname.replace(CORE_EXCHANGE_DOCS_BASE_URL, '');
  const routeSection = stripTrailingSlash(
    routeWithoutBase.split('/').slice(0, 2).join('/'),
  );

  for (const section of ROUTES) {
    if (section.children == null) continue;
    const match = section.children.find(
      (child) => stripTrailingSlash(child.path) === routeSection,
    );
    if (match != null) {
      // Items with subNav: false (e.g. Welcome) should show the top-level nav
      if (match.subNav === false) return { currentItem: match };
      // Items with their own children (e.g. Authentication, API reference)
      // show the parent section with all siblings, but keep the matched
      // item's children so ListDropdown renders them as an expandable group
      if (match.children != null) {
        const flatChildren = match.children.flatMap(
          (group) => group.children ?? [group],
        );
        const expandedMatch = { ...match, children: flatChildren };
        const sectionChildren = section.children.map((child) =>
          child === match ? expandedMatch : child,
        );
        return {
          parentSection: {
            title: section.title,
            icon: section.icon,
            children: [{ children: sectionChildren }],
          },
        };
      }
      // Return the section, reshaped so SidebarSubnav renders
      // the sibling pages as a flat list under the section header.
      return {
        parentSection: {
          title: section.title,
          icon: section.icon,
          children: [{ children: section.children }],
        },
      };
    }
  }
  return {};
};

const Sidebar: React.FC<Props> = (props) => {
  const BASE_URL = props.baseUrl ?? CORE_EXCHANGE_DOCS_BASE_URL;
  const router = useRouter();
  const [route, setRoute] = useState<SidebarRoute>(
    resolveRoute(router.pathname),
  );

  useEffect(() => {
    setRoute(resolveRoute(router.pathname));
  }, [router.pathname]);

  if (props.mobile && props.closed) {
    return <></>;
  }

  return (
    <aside className={cx(props.className, sidebarStyles.sidebar)}>
      <nav aria-label='Main' className={sidebarStyles.nav}>
        {props.mobile && (
          <div className={sidebarStyles.exchangeClose}>
            <IconButton
              accessibilityLabel='Close nav'
              icon={<CloseS1 />}
              onClick={() => props.onClose?.()}
            />
          </div>
        )}
        {!props.mobile && (
          <div className={sidebarStyles.header}>
            <Link
              href={BASE_URL}
              rel='noopener noreferrer'
              className={sidebarStyles.logo}
            >
              <Image
                className={sidebarStyles.logoImage}
                src='/assets/img/navbar/logo.svg'
                alt='Plaid logo'
                width='80'
                height='30.47'
                noMarginBottom
              />
              <span className={sidebarStyles.logoText}>Core Exchange</span>
            </Link>
          </div>
        )}
        <div className={sidebarStyles.shrinkContainer}>
          {route.parentSection != null ? (
            <SidebarSubnav
              currentRoute={route.parentSection}
              baseUrl={BASE_URL}
              onClose={props.onClose ?? (() => null)}
              mobile={props.mobile}
            />
          ) : (
            <SidebarNavGroup
              baseUrl={BASE_URL}
              dark={false}
              navGroup={ROUTES}
              currentRoute={route.currentItem}
              isCollapsible={false}
            />
          )}
        </div>
      </nav>
    </aside>
  );
};

Sidebar.displayName = 'Sidebar';
export default Sidebar;
